/**
 * 検索ヒットの誌面ハイライト（FR-SEARCH-01）。
 *
 * ホスト上に重ねる DOM オーバーレイ。エンジン非依存で、ページの画面矩形
 * （engine.pageRect）とヒットの 0–1 正規化矩形から絶対座標の矩形を並べる。
 * WebGL キャンバスの上にも DOM を重ねられるため 2D/3D の両エンジンで同じ実装が効く。
 * ページ遷移・ズーム・パン・リサイズのたびに再配置する（呼び出し側が render を呼ぶ）。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { SearchHit, SearchRect } from '../core/events';
import type { ScreenRect } from '../engine/layout';

/** ページ番号 → そのページの画面矩形（表示外は null）。 */
export type RectResolver = (pageNo: number) => ScreenRect | null;

export class HighlightLayer {
  private readonly el: HTMLDivElement;
  /** ページ番号 → 0–1 正規化矩形群。 */
  private readonly byPage = new Map<number, readonly SearchRect[]>();

  constructor(host: HTMLElement, registry: DisposeRegistry) {
    const doc = host.ownerDocument;
    this.el = doc.createElement('div');
    this.el.className = cls('search-highlights');
    this.el.setAttribute('aria-hidden', 'true');
    host.appendChild(this.el);
    registry.add('other', () => this.el.remove());
  }

  /** ヒット群を取り込む（矩形の無いヒットは無視）。以後 render で表示される。 */
  setHits(hits: readonly SearchHit[]): void {
    this.byPage.clear();
    for (const hit of hits) {
      if (hit.rects && hit.rects.length > 0) {
        this.byPage.set(hit.page, hit.rects);
      }
    }
  }

  /** 現在の表示に合わせて矩形を配置し直す。表示中ページのヒットだけ描く。 */
  render(rectOf: RectResolver): void {
    const doc = this.el.ownerDocument;
    const fragment = doc.createDocumentFragment();
    for (const [pageNo, rects] of this.byPage) {
      const pageRect = rectOf(pageNo);
      if (!pageRect) continue;
      for (const rect of rects) {
        const box = doc.createElement('div');
        box.className = cls('search-highlight');
        box.style.left = `${pageRect.x + rect.x * pageRect.width}px`;
        box.style.top = `${pageRect.y + rect.y * pageRect.height}px`;
        box.style.width = `${rect.w * pageRect.width}px`;
        box.style.height = `${rect.h * pageRect.height}px`;
        fragment.appendChild(box);
      }
    }
    this.el.replaceChildren(fragment);
  }

  /** 表示だけ消す（データは保持・めくり中の一時退避に使う）。 */
  hide(): void {
    this.el.replaceChildren();
  }

  /** ハイライトを消す（データも破棄）。 */
  clear(): void {
    this.byPage.clear();
    this.el.replaceChildren();
  }

  /** 現在ハイライト対象のページがあるか（render 要否の判定に使う）。 */
  get active(): boolean {
    return this.byPage.size > 0;
  }
}
